import { computed, markRaw } from 'vue'
import { useRouter } from 'vue-router'

import { ROUTE_NAME } from '@/const'

import { useDashboardsStore } from '@/store/dashboards'
import { useModalsStore } from '@/store/modals'
import { useRepositoriesStore } from '@/store/repositories'

import { DSConfirmationDialog, DSToast } from '@ds'

export const useDashboard = (slug?: string) => {

  const router = useRouter()

  const dashboardsStore = useDashboardsStore()
  const modalsStore = useModalsStore()
  const repositoriesStore = useRepositoriesStore()
  
  const dashboard = computed(() => dashboardsStore.getList?.find(item => item.slug === slug))

  const getDashboardTabKey = (slug: string) =>
    `${ROUTE_NAME.DASHBOARDS_DASHBOARD}${JSON.stringify({ slug })}`

  const insertClonedDashboardTab = async (
    sourceSlug: string,
    clonedSlug: string,
  ) => {
    const sortedTabs = [...repositoriesStore.getSortedTabs]
    if (!sortedTabs.length) return

    const sourceKey = getDashboardTabKey(sourceSlug)
    const newKey = getDashboardTabKey(clonedSlug)
    const sourceIndex = sortedTabs.indexOf(sourceKey)
    if (sourceIndex >= 0) {
      sortedTabs.splice(sourceIndex + 1, 0, newKey)
    } else {
      sortedTabs.push(newKey)
    }
    await repositoriesStore.sortTabs(sortedTabs)
  }

  const configureDashboard = () => {
    if (!slug) return
    dashboardsStore.setEditMode(true)
    router.push({
      name: ROUTE_NAME.DASHBOARDS_DASHBOARD,
      params: { slug },
    })
  }

  const duplicateDashboard = async () => {
    if (dashboardsStore.getEditMode || !dashboard.value || !slug) return
    try {
      const cloned = await dashboardsStore.cloneView(dashboard.value)
      await insertClonedDashboardTab(slug, cloned.slug)
      await router.push({
        name: ROUTE_NAME.DASHBOARDS_DASHBOARD,
        params: { slug: cloned.slug },
      })
      DSToast.success(`Dashboard "${cloned.name}" created`, {
        position: 'top-right',
        duration: 5000,
      })
    } catch {
      // store and api interceptors surface errors
    }
  }

  const removeDashboard = () => {
    if (!slug || !dashboard.value) return
    const modalInstance = modalsStore.init(
      'dashboard-remove-confirmation-dialog',
      markRaw(DSConfirmationDialog),
    )
    modalInstance?.open(modalsStore.getZIndex(), {
      title: `Remove dashboard "${dashboard.value.name || 'Untitled'}"`,
      description: `"${dashboard.value.name || 'Untitled'}" will be permanently removed. This cannot be undone.`,
      label: 'Remove',
      onConfirm: () => {
        dashboardsStore.remove(dashboard.value!.id)
      },
    })
  }

  return {
    configureDashboard,
    duplicateDashboard,
    removeDashboard
  }
}
